import React from "react";
import { PdfSlateBaseParentBlockNode, PdfSlateEndnote, PdfSlateImage, PdfSlateLink, PdfSlateListBlock, PdfSlateTextNode, PdfTheme, PdfSlateProfileLinks, PdfEndnoteSubheading, PdfChapterEndnotes } from "../types";
import { ADDITIONAL_CHAPTER_MARGINS, CODE_BLOCK_MARGIN, MEDIUM_MARGIN, SMALL_MARGIN } from "../configs";
import BaseChildNode from "./BaseChildNode";
import Paragraph from "./Paragraph";
import OrnamentalBreak from "./OrnamentalBreak";
import Image from "./Image";
import EndNotes from "./EndNotes";
import List from "./List";
import SocialProfile from "./SocialProfiles";
import { FootnotePlaceholder } from "@surge-global-engineering/react-pdf-components";
import { getAllEndNotesOfChapter } from "../../../../utils/get-chapter-endnotes";
import { View as RPDFView } from "@surge-global-engineering/rpdf-renderer";
import { Chapter, ChapterConfiguration } from "../../../../types/chapter";
import { SlateBlockquote, SlateCalloutBox } from "../../../../types/slate";
import { Blockquote } from "./Blockquote";
import { TextMessages } from "./TextMessages";
import { TextMessagesGroupNode } from "../../../Plate/plugins/text-message/types";
import { CalloutBox } from "./calloutbox";

interface ChapterBodyProps {
    theme: PdfTheme;
    lightText?: boolean;
    endNoteOffset: number
    allEndNotes?: (PdfSlateEndnote | PdfEndnoteSubheading | PdfChapterEndnotes)[];
    chapterLevelConfig: ChapterConfiguration;
    chapter?: Chapter
}

type PdfSlateBaseParentBlockNodeType = Pick<PdfSlateBaseParentBlockNode, "type">["type"]

const getChildrenTypes = (children: PdfSlateBaseParentBlockNode[]) => {
    const types: string[] = [];
    for (const child of children) {
        const { children, type } = child;
        if (!type) {
            return types;
        }
        types.push(type);
        const childrentypes = getChildrenTypes(children as PdfSlateBaseParentBlockNode[]);
        types.push(...childrentypes);
    }
    return types;
};

const isParagraphAfterSubheading = (previousChildType: PdfSlateBaseParentBlockNodeType, currentChildType: PdfSlateBaseParentBlockNodeType) => {
    if (previousChildType === "h2" || previousChildType === "h3" || previousChildType === "h4" || previousChildType === "h5" || previousChildType === "h6") {
        if (currentChildType === "p") {
            return true;
        }
    }
    return false;
};

const ChapterBody: React.FC<ChapterBodyProps> = ({ theme, lightText = false, children, endNoteOffset, allEndNotes, chapterLevelConfig, chapter  }) => {
    const _children = children as (PdfSlateBaseParentBlockNode | PdfSlateTextNode | PdfSlateListBlock | PdfSlateEndnote | PdfSlateImage | PdfSlateLink | PdfSlateProfileLinks)[];
    const { paragraph, beginFirstSentence } = theme.properties;

    const { showFirstSentenceFormatting = true } = chapterLevelConfig;

    if (!children) return <></>;

    while(_children.length > 0) {
        const { children, type } = _children[_children.length-1] as PdfSlateBaseParentBlockNode;

        try{
            const { text } = children[0] as PdfSlateTextNode;
            if(children.length == 1 && type === "p" && text === ""){
                _children.pop();
            }
            else{
                break;
            }
        }
        catch(error){
            break;
        }
    }

    const endNotes = getAllEndNotesOfChapter(_children as PdfSlateBaseParentBlockNode[]);
    let _nodeIndex = 0;
    let previousChildType: PdfSlateBaseParentBlockNodeType;
    let childTypeBeforeScene: PdfSlateBaseParentBlockNodeType;
    return (
        <>
            {_children.map((child, index) => {        
                const { children, type } = child as PdfSlateBaseParentBlockNode;
                const nodeIndex = _nodeIndex;
                const subheadingTypes = ["h2", "h3", "h4", "h5", "h6"];
                let disableIndent = false;
                let isAfterSceneBreak = false; 

                if (["align_center", "align_right"].includes(type)) {
                    const textAlign = type === "align_center" ? "center" : "right";
                    const chldrenTypes = getChildrenTypes(children as PdfSlateBaseParentBlockNode[]);
                    if (!["h2", "blockquote", "code_block", "ul", "ol"].map(c => chldrenTypes.includes(c)).filter(Boolean).length) {
                        if (_nodeIndex !== 0) {
                            _nodeIndex++;
                        }
                    }
                    // `align_center`, `align_right` types can have `h2` type as a child.
                    // assign last element of `chldrenTypes` as a `previousChildType` because last element of `chldrenTypes` effect the next child in this scenario
                    previousChildType = chldrenTypes[chldrenTypes.length - 1] as PdfSlateBaseParentBlockNodeType;
                    return (
                      <BaseChildNode
                        key={`para-base-${index}`}
                        theme={theme}
                        nodeIndex={nodeIndex}
                        endNotes={endNotes}
                        parentType={type}
                        style={{
                          textAlign,
                          marginBottom: paragraph.indent ? "" : SMALL_MARGIN,
                        }}
                        showFirstSentenceFormatting={showFirstSentenceFormatting}
                        endNoteOffset={endNoteOffset}
                      >
                        {children}
                      </BaseChildNode>
                    );
                }

                if (type === "calloutbox") {
                  previousChildType = type;
                  const { background, border } = child as SlateCalloutBox;
                  return (
                    <CalloutBox
                      key={`calloutbox-${index}`}
                      theme={theme}
                      nodeIndex={nodeIndex}
                      endNotes={endNotes}
                      parentType={type}
                      showFirstSentenceFormatting={false}
                      index={index}
                      background={background}
                      border={border}
                    >
                      {children}
                    </CalloutBox>  
                  );
                }

                if (type === "blockquote") {
                    previousChildType = type;

                    return (
                        <Blockquote
                          index={index}
                          key={`blockquote-component-${index}`}
                          nodeIndex={nodeIndex}
                          theme={theme}
                          endNotesOffset={endNoteOffset}
                          endNotes={endNotes}
                          blockquoteNode={child as SlateBlockquote}
                          parentType={type}
                        >
                          {children}
                        </Blockquote>
                    );
                }

                if (type === "text_messages") {
                  previousChildType = type;
                  return (
                      <TextMessages
                        index={index}
                        key={`text-messages-${index}`}
                        nodeIndex={nodeIndex}
                        theme={theme}
                        endNotesOffset={endNoteOffset}
                        endNotes={endNotes}
                        parentType={type}
                        style = {(child as TextMessagesGroupNode).style}
                      >
                        {children}
                      </TextMessages>
                  );
              }

                if (type === "code_block") {
                    previousChildType = type;
                    return (
                      <Paragraph
                        key={`blockquote-${index}`}
                        theme={theme}
                        endNotesOffset={endNoteOffset}
                        nodeIndex={nodeIndex + 10}
                        endNotes={endNotes}
                        indented={false}
                        parentType={type}
                        style={{
                          marginTop: MEDIUM_MARGIN,
                          marginBottom: MEDIUM_MARGIN,
                          marginLeft: CODE_BLOCK_MARGIN,
                          marginRight: CODE_BLOCK_MARGIN,
                        }}
                        showFirstSentenceFormatting={showFirstSentenceFormatting}
                      >
                        {children}
                      </Paragraph>
                    );
                }

                if (type === "indent") {
                  previousChildType = type;
                  return (
                    <Paragraph
                      key={`indent-${index}`}
                      theme={theme}
                      endNotesOffset={endNoteOffset}
                      nodeIndex={nodeIndex + 10}
                      endNotes={endNotes}
                      indented={false}
                      isHangingIndent={true}
                      parentType={type}
                      style={{
                        paddingLeft: `${theme.properties.hangingIndent}cm`,
                      }}
                      canJustifyText={true}
                      showFirstSentenceFormatting={false}
                    >
                      {children}
                    </Paragraph>
                  );
              }

                if (type === "h2" || type === "h3" || type === "h4" || type === "h5" || type === "h6") {
                    const marginTop = paragraph.indent ? MEDIUM_MARGIN : SMALL_MARGIN;
                    const key = `para-${type}-${index}`;
                    const fontWeight = type==="h2" ? 900 : 600;
                    return (
                      <Paragraph
                        key= {key}
                        theme={theme}
                        endNotesOffset={endNoteOffset}
                        nodeIndex={nodeIndex + 10}
                        parentType={type}
                        endNotes={endNotes}
                        style={{
                          marginTop,
                          marginBottom: SMALL_MARGIN,
                          fontWeight: fontWeight,
                        }}
                        canJustifyText={false}
                        showFirstSentenceFormatting={showFirstSentenceFormatting}
                      >
                        {children}
                      </Paragraph>
                    );
                }
                
                if (type === "ornamental-break") {
                    if (theme.properties.sceneBreakShowSetting === "no-break") {
                      return <></>;
                    }
                    if (_nodeIndex !== 0) {
                        _nodeIndex++;
                    }
                    if (beginFirstSentence === "break") {
                        _nodeIndex = 0;
                    }
                    previousChildType = type;
                    return <OrnamentalBreak key={`ornamental-break-${index}`} lightText={lightText} theme={theme} />;
                }

                if (index > 0 && (_children[index - 1] as PdfSlateBaseParentBlockNode).type === "ornamental-break" && theme.properties.sceneBreakShowSetting !== "no-break") {
                  isAfterSceneBreak = true;
                }

                if (type === "scene") {

                  if (previousChildType === "ornamental-break") {
                    if (theme.properties.sceneBreakShowSetting !== "no-break") {
                      if (_nodeIndex !== 0) {
                        _nodeIndex++;
                      }
                      if (beginFirstSentence === "break") {
                        _nodeIndex = 0;
                      }
                    }
                  }

                  childTypeBeforeScene = previousChildType;
                  previousChildType = type;
                  
                  _nodeIndex++;
                  
                  return (
                    <BaseChildNode
                      key={`scene-${index}`}
                      sceneIndex={index}
                      previousNode={childTypeBeforeScene}
                      theme={theme}
                      nodeIndex={nodeIndex}
                      endNotes={endNotes}
                      parentType={type}
                      style={{
                        marginBottom: paragraph.indent ? "" : SMALL_MARGIN,
                      }}
                      isAfterSceneBreak={isAfterSceneBreak}
                      showFirstSentenceFormatting={showFirstSentenceFormatting}
                      noIndent={disableIndent}
                    >
                      {children}
                    </BaseChildNode>
                  );
                }

                if (type === "page-break"){
                    previousChildType = type;

                    const lastOnPage = (_children[index+1] === undefined && _children[index+2] === undefined);
                    if (!lastOnPage) {
                        return <RPDFView key={`page-break-${index}`} break/>;
                    }
                }

                if (type === "image") {
                    const imageNode = child as PdfSlateImage;
                    previousChildType = type;

                    if(imageNode.separatePage) {
                        return (
                            <Image
                                key={`image-${index}`}
                                theme={theme}
                                lightTextCaption={lightText}
                                imageNode={imageNode}
                                priorNode={_children[index-2]}
                                previousNode={_children[index-1]}
                                nextNode={_children[index+1]}
                                thenNode={_children[index+2]}
                            />
                        );
                    } else {
                        return (
                            <Image key={`image-${index}`} theme={theme} lightTextCaption={lightText} imageNode={imageNode} />
                        );
                    }
                }

                if (type === "profile") {
                    const profileNode = child as PdfSlateProfileLinks;
                    return (
                        <SocialProfile key={`profile-${index}`} theme={theme} profileNode={profileNode}></SocialProfile>
                    );
                }

                if (["ul", "ol"].includes(type)) {
                    previousChildType = type;
                    return (
                      <List
                        endnotes={endNotes}
                        key={`li-${index}`}
                        theme={theme}
                        type={type as "ol" | "ul"}
                        showFirstSentenceFormatting={showFirstSentenceFormatting}
                        endNoteOffset={endNoteOffset}
                      >
                        {children}
                      </List>
                    );
                }

                // if (type === "align_left") { }

                _nodeIndex++;

                // disable indent following ornamental-break
                if (index > 0 && (_children[index - 1] as PdfSlateBaseParentBlockNode).type === "ornamental-break" && theme.properties.sceneBreakShowSetting !== "no-break") {
                    disableIndent = true;
                }

                // disable indent following subheading
                if (index > 0 && subheadingTypes.includes((_children[index - 1] as PdfSlateBaseParentBlockNode).type)) {
                  disableIndent = true;
                }

                const _previousChildType = previousChildType;
                previousChildType = type;  
                return (
                  <Paragraph
                    key={`para-base-${index}`}
                    theme={theme}
                    nodeIndex={nodeIndex}
                    endNotes={endNotes}
                    endNotesOffset={endNoteOffset}
                    disableIndent={disableIndent}
                    style={{
                      marginBottom: paragraph.indent ? "" : SMALL_MARGIN,
                    }}
                    indented={
                      isParagraphAfterSubheading(_previousChildType, type)
                        ? false
                        : undefined
                    }
                    showFirstSentenceFormatting={showFirstSentenceFormatting}
                  >
                    {children}
                  </Paragraph>
                );
            })}            
            {(!theme.properties.notesMode || theme.properties.notesMode === "END_OF_CHAPTER") && endNotes.length > 0 && <EndNotes content={endNotes} theme={theme} nodeIndex={_nodeIndex} endnoteOffset={endNoteOffset} chapter={chapter}/>}

            {theme.properties.notesMode === "END_OF_BOOK" && allEndNotes && allEndNotes.length > 0 && <EndNotes content={allEndNotes} endnoteOffset={0} theme={theme}  nodeIndex={_nodeIndex} chapter={chapter}/>}

            {theme.properties.notesMode === "FOOTNOTE" && (
                <FootnotePlaceholder
                    marginTop={0-(theme.properties.margin.bottom + ADDITIONAL_CHAPTER_MARGINS.bottom)}
                    style={{fontSize: theme.properties.baseFontSize, fontFamily: theme.properties.printBaseFont}}
                />
            )}
        </>
    );
};

export default ChapterBody;
